import { AnimatedGaugeChart } from '@/components/ui/AnimatedGaugeChart'
import { MarketIndicatorBaseProps } from './MarketIndicatorBase'

export const MarketIndicatorMobile = ({ 
  buyerPercentage = 75,
  sentiment = 'Greed',
  priceSpread = 8.2,
  dealSpread = 51,
  loading = false
}: MarketIndicatorBaseProps) => {
  if (loading) {
    return (
      <div className="h-full flex flex-col relative">
        <p 
          className="absolute font-plus-jakarta font-medium leading-[24px] left-[23px] text-[#2d2923] dark:text-[#fdfdfd] text-[16px] top-[23px] uppercase"
          
        >
          Market indicator
        </p>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading market data...
          </div>
        </div>
      </div>
    )
  }

  // Calculate progress bar width (clamped to 100%)
  const priceSpreadWidth = Math.min(Math.abs(priceSpread), 100)
  const dealSpreadWidth = Math.min(Math.abs(dealSpread), 100)

  // Calculate minimum height based on content positions
  // Cards start at top-[216px], each card is ~114px, gap is 12px
  const minHeight = 216 + 114 + 12 + 114 // ~456px

  return (
    <div className="relative w-full" style={{ minHeight: `${minHeight}px`, height: '100%' }}>
      {/* Header - positioned exactly as in Figma: accounting for p-6 padding (24px) */}
      <p 
        className="absolute font-plus-jakarta font-medium leading-[24px] left-0 text-[#2d2923] dark:text-[#fdfdfd] text-[16px] top-0 uppercase"
        
      >
        Market indicator
      </p>

      {/* Gauge - positioned exactly as in Figma: accounting for p-6 padding (24px) */}
      <div className="absolute left-0 top-[40px] w-[160px] h-[160px] z-10">
        <AnimatedGaugeChart
          value={buyerPercentage}
          max={100}
          min={0}
          gaugePrimaryColor="#C4E804"
          gaugeSecondaryColor="#ffffff"
          className="w-full h-full"
        />
      </div>

      {/* Description - positioned exactly as in Figma: accounting for p-6 padding (24px) */}
      <p 
        className="absolute font-plus-jakarta font-normal leading-[24px] left-[176px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)] text-[16px] top-[48px] w-[153px] whitespace-pre-wrap"
        
      >
        Market sentiment indicator for buyers/sellers ratio
      </p>

      {/* Sentiment - positioned exactly as in Figma: accounting for p-6 padding (24px) */}
      <p 
        className="absolute font-plus-jakarta font-normal leading-[48px] left-[176px] text-[#2d2923] dark:text-[#fdfdfd] text-[28px] top-[136px] tracking-[-0.52px]"
        
      >
        {sentiment}
      </p>

      {/* Buyer label - positioned exactly as in Figma: accounting for p-6 padding (24px) */}
      <p 
        className="absolute font-plus-jakarta font-medium leading-[20px] left-0 text-[#2d2923] dark:text-[#fdfdfd] text-[14px] top-[168px]"
        
      >
        Buyer
      </p>

      {/* Seller label - positioned exactly as in Figma: accounting for p-6 padding (24px) */}
      <p 
        className="absolute font-plus-jakarta font-medium leading-[20px] left-[149px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px] text-right top-[168px] translate-x-[-100%]"
        
      >
        Seller
      </p>

      {/* Spread Cards Container - positioned exactly as in Figma: accounting for p-6 padding (24px) */}
      <div className="absolute left-0 top-[216px] w-full max-w-[338px] flex flex-col gap-[12px]">
        {/* Price Spread Card */}
        <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-[12px] p-[16px]">
          <p 
            className="font-plus-jakarta font-semibold leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]"
            
          >
            Price Spread
          </p>
          <div className="flex flex-col gap-[6px]">
            <div className="flex items-center justify-between">
              <p 
                className="font-plus-jakarta font-medium leading-[28px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]"
                
              >
                <span className="text-[20px]">{Math.abs(priceSpread).toFixed(1)} </span>
                <span className="text-[12.9px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
              </p>
              <div className="h-[28px] overflow-clip relative w-[172px]">
                {/* Background pattern */}
                <div 
                  className="absolute h-[20px] right-0 top-[4px] w-[177px] bg-[rgba(45,41,35,0.05)] dark:bg-[rgba(253,253,253,0.05)]" 
                  style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(45,41,35,0.15) 1px, rgba(45,41,35,0.15) 2px)'
                  }}
                />
                {/* Progress bar */}
                <div 
                  className="absolute bg-[#e3fd5a] h-[20px] left-0 top-[4px]"
                  style={{ width: `${priceSpreadWidth}%` }}
                />
              </div>
            </div>
            <p 
              className="font-plus-jakarta font-normal leading-[16px] text-[12px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-pre-wrap"
              
            >
              Difference between buy and sell prices
            </p>
          </div>
        </div>

        {/* Deal Size-Spread Card */}
        <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-[12px] p-[16px]">
          <p 
            className="font-plus-jakarta font-semibold leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]"
            
          >
            Deal Size-Spread
          </p>
          <div className="flex flex-col gap-[6px]">
            <div className="flex items-center justify-between">
              <p 
                className="font-plus-jakarta font-medium leading-[28px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]"
                
              >
                <span className="text-[20px]">{Math.abs(dealSpread).toFixed(1)}</span>
                <span className="text-[20px]"> </span>
                <span className="text-[12.9px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
              </p>
              <div className="h-[28px] overflow-clip relative w-[172px]">
                {/* Background pattern */}
                <div 
                  className="absolute h-[20px] right-0 top-[4px] w-[177px] bg-[rgba(45,41,35,0.05)] dark:bg-[rgba(253,253,253,0.05)]" 
                  style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(45,41,35,0.15) 1px, rgba(45,41,35,0.15) 2px)'
                  }}
                />
                {/* Progress bar */}
                <div 
                  className="absolute bg-[#e3fd5a] h-[20px] left-0 top-[4px]"
                  style={{ width: `${dealSpreadWidth}%` }}
                />
              </div>
            </div>
            <p 
              className="font-plus-jakarta font-normal leading-[16px] text-[12px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-pre-wrap"
              
            >
              Difference between buy and sell volume
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

